import { useId, useState, type FormEvent } from "react";
import { MUTATIONS, useMutation } from "../../../api";
import type { WorkshopId } from "../../../api/workshop";
import type { SmallTeamId } from "../../../api/smallTeam";
import Dialog from "../../../design-system/primitives/components/Dialog/Dialog";
import TextInput from "../../../design-system/primitives/components/TextInput/TextInput";
import Button from "../../../design-system/primitives/components/Button/Button";
import Text from "../../../design-system/primitives/components/Text/Text";
import styles from "./RenameGroupDialog.css";

function validateGroupName(name: string): string | null {
  if (name.length < 2) {
    return "Gruppenname ist zu kurz. Der Name muss mindestens zwei Zeichen haben.";
  }
  if (name.length > 64) {
    return "Gruppenname ist zu lang. Der Name darf nicht länger als 64 Zeichen sein.";
  }
  if (!/^[ -~]*$/.test(name)) {
    return "Diese Eingabe ist nicht zulässig. Verwende bitte keine Sonderzeichen.";
  }
  return null;
}

type RenameGroupDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: WorkshopId;
  smallTeamId: SmallTeamId;
  currentName: string;
};

/**
 * Renaming lives behind the group card's menu, per the Figma redesign: a
 * small dialog with a text input, like the page's other dialogs.
 */
const RenameGroupDialog = ({
  open,
  onOpenChange,
  workshopId,
  smallTeamId,
  currentName,
}: RenameGroupDialogProps) => {
  const errorId = useId();
  const formId = useId();
  const [value, setValue] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const [doUpdateSmallTeamInfo] = useMutation(MUTATIONS.updateSmallTeamInfo);

  // Resets the draft whenever the dialog transitions to open, discarding any
  // half-typed input left over from a previous time it was opened. Adjusted
  // during render (React's documented pattern for this) rather than in an
  // effect, since an effect here would just trigger a redundant extra render.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setValue(currentName);
      setError(null);
    }
  }

  const handleSave = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateGroupName(value);
    if (validationError) {
      setError(validationError);
      return;
    }

    doUpdateSmallTeamInfo({ workshopId, smallTeamId, name: value })
      ?.then(() => {
        onOpenChange(false);
      })
      .catch((mutationError: unknown) => {
        console.error("Renaming group failed", mutationError);
      });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Gruppe umbenennen"
      description="Gib einen neuen Namen für die Gruppe ein."
      closeAriaLabel="Schließen"
      contentSlot={
        // The Dialog renders its footer outside this slot, so the save button
        // joins the form via its `form` attribute rather than by nesting.
        <form id={formId} className={styles.form} onSubmit={handleSave}>
          <TextInput
            name="group-name"
            label="Gruppenname"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="Gruppenname"
            error={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
          />
          {error && (
            <Text id={errorId} variant="body-small" color="neutral-100">
              {error}
            </Text>
          )}
        </form>
      }
      footer={
        <Button
          title="Speichern"
          color="accent"
          size="large"
          type="submit"
          form={formId}
          fullWidth
        />
      }
    />
  );
};

export default RenameGroupDialog;
